import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from '../../../../../redux/connect';
import { openDocumentSelectionModal, closeDocumentSelectionModal, fetchProjectFolders, fetchFolderContents, createAttachmentFromDocument } from '../../../../../redux/actions';
import AttachmentUploadThunk from '../../../../../redux/thunk/attachmentUploadThunk';
import { attachmentOrigin } from '../../../../../constants/attachment-types';
import {
  isDocumentSelectionOpen,
  isUploadingAttachments,
  getCurrentPreviewIssueId,
  getCurrentPreviewIssueAttributes,
} from '../../../../../redux/selectors';
import { v4 as getUUID } from 'uuid';
import { IconButton, SvgIcon, Loader, DocumentSelectionModal } from '@adsk/bim360-matrix-react-components';
import { filterCurrentDocument, getAllowedFiles } from '../../../../../utils/attachmentsHelper';
import paperClip from '../../../../../assets/images/icons/icon-paperclip.svg';
import { createGetFolderContentsMethod, createHasMoreFolderContents } from '../../../../../utils/docsApiHelpers';

export class PureAttachmentUpload extends PureComponent {

  constructor() {
    super();
    this.onCloseDocumentsModal = this.onCloseDocumentsModal.bind(this);
    this.openDocumentSelectionModal = this.openDocumentSelectionModal.bind(this);
    this.handleChoosingDocument = this.handleChoosingDocument.bind(this);
    this.handleUploadFiles = this.handleUploadFiles.bind(this);
    this.getProjectFolders = this.getProjectFolders.bind(this);
    this.getFolderContents = this.getFolderContents.bind(this);
    this.hasMoreFolderContents = this.hasMoreFolderContents.bind(this);
  }

  openDocumentSelectionModal() {
    this.props.openDocumentSelectionModal();
  }

  onCloseDocumentsModal() {
    this.props.closeDocumentSelectionModal();
  }

  handleChoosingDocument(documentUrn, document) {
    const { issueId, createAttachmentFromDocument } = this.props;

    createAttachmentFromDocument(document, issueId, attachmentOrigin.CHANGESET);
  }

  handleUploadFiles(files) {
    const { issueId, uploadAttachmentFiles } = this.props;

    uploadAttachmentFiles([...files].map(file => { return { id: getUUID(), file }; }), issueId, true, attachmentOrigin.CHANGESET);
  }

  getProjectFolders() {
    // This method is called every time we load the tree from scratch. That's why we need to clean the cache here - for it to be relevant for the current tree.
    const documentsCache = {};
    this.getFolderContentsWrapped = createGetFolderContentsMethod(documentsCache, this.props.fetchFolderContents);
    this.hasMoreFolderContentsWrapped = createHasMoreFolderContents(documentsCache);
    return new Promise((resolve, reject) => {
      this.props.fetchProjectFolders(resolve, reject);
    });
  }

  getFolderContents(urn) {
    return this.getFolderContentsWrapped(urn).then(data => filterCurrentDocument(data, this.props.targetUrn));
  }

  hasMoreFolderContents(context) {
    return this.hasMoreFolderContentsWrapped(context);
  }

  renderUploadButton() {
    return (
      <IconButton className="AttachmentUpload__button" onClick={this.openDocumentSelectionModal}>
        <SvgIcon width="15px" height="15px">
          {paperClip}
        </SvgIcon>
      </IconButton >
    );
  }

  render() {
    const { isDocumentSelectionOpen, container, isSaving } = this.props;
    const showLoader = isDocumentSelectionOpen || isSaving;
    const classes = classNames('AttachmentUpload', {
      'AttachmentUpload--loading': showLoader,
    });

    return (
      <div className={classes}>
        {showLoader ? <Loader /> : this.renderUploadButton()}
        <DocumentSelectionModal
          container={container}
          allowUploadMultipleFiles
          isModalOpen={isDocumentSelectionOpen}
          isSaving={isSaving}
          closeModal={this.onCloseDocumentsModal}
          allowUploadFiles
          acceptFileTypes={getAllowedFiles()}
          onChoosingDocument={this.handleChoosingDocument}
          uploadFiles={this.handleUploadFiles}
          getRootFolders={this.getProjectFolders}
          getFolderContents={this.getFolderContents}
          hasMoreFolderContents={this.hasMoreFolderContents}
        />
      </div>
    );
  }
}

PureAttachmentUpload.displayName = 'AttachmentUpload';

PureAttachmentUpload.propTypes = {
  issueId: PropTypes.string.isRequired,
  targetUrn: PropTypes.string,
  container: PropTypes.object.isRequired,
  openDocumentSelectionModal: PropTypes.func.isRequired,
  closeDocumentSelectionModal: PropTypes.func.isRequired,
  fetchProjectFolders: PropTypes.func.isRequired,
  fetchFolderContents: PropTypes.func.isRequired,
  createAttachmentFromDocument: PropTypes.func.isRequired,
  uploadAttachmentFiles: PropTypes.func.isRequired,
  isDocumentSelectionOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  const issueId = getCurrentPreviewIssueId(state);
  const issue = getCurrentPreviewIssueAttributes(state);
  const { targetUrn } = issue;

  return {
    issueId,
    isDocumentSelectionOpen: isDocumentSelectionOpen(state),
    isSaving: isUploadingAttachments(state, attachmentOrigin.CHANGESET),
    targetUrn,
  };
};

const mapDispatchToProps = {
  openDocumentSelectionModal,
  closeDocumentSelectionModal,
  fetchProjectFolders,
  fetchFolderContents,
  createAttachmentFromDocument,
  uploadAttachmentFiles: AttachmentUploadThunk.uploadAttachmentFiles,
};

export default connect(mapStateToProps, mapDispatchToProps)(PureAttachmentUpload);

